import { HttpBackend, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { Observable, throwError, timer } from 'rxjs';
import { map, retry } from 'rxjs/operators';

import { LocalStorageService } from './local-storage.service';

interface TrpcResponse<T> {
	result: { data: T };
}

export interface HelixSession {
	token: string;
	partnerName: string;
}

/**
 * Storage keys helix reads at boot. `activeSession` is the canonical pair; `token` and
 * `partnerName` are the mirrors helix keeps for its own legacy readers and for this app's
 * `AuthService`, which reads `token`. MUST match `apps/selfservice/src/utils/resolve-initial-session.ts`
 * in helix.
 */
export const HELIX_SESSION_STORAGE_KEY = 'activeSession';

const isRetryable = (error: unknown): boolean =>
	error instanceof HttpErrorResponse && (error.status === 0 || error.status >= 500);

/** The swap answered 200 without a token. Counts as a swap failure, not a storage one. */
export class HelixSwapError extends Error {
	constructor() {
		super('Helix token swap returned no token');
	}
}

@Injectable({
	providedIn: 'root',
})
export class HelixHandoffService {
	// Built on the backend, past both app interceptors: the swap must not carry whatever bearer this
	// origin holds (possibly another customer's helix token), and its 401 is about the partner token,
	// not this app's session, so it must not log the customer out.
	private readonly http = new HttpClient(inject(HttpBackend));
	private readonly localStorage = inject(LocalStorageService);

	/**
	 * `partnerId` is the backend partner id (`clientSettings.id`), which the BFF forwards to Froda as
	 * the partner the token must belong to. It is not the `key` helix boots under: `lunar-se` is the key,
	 * `lunar` the partner.
	 */
	swapPartnerToken({ partnerToken, partnerId }: { partnerToken: string; partnerId: string }): Observable<string> {
		return this.http
			.post<TrpcResponse<{ token: string }>>(
				`${environment.standalone.bffHost}/identity.swapPartnerToken`,
				{ token: partnerToken },
				{ headers: { 'Content-Type': 'application/json', 'x-partner-id': partnerId } }
			)
			.pipe(
				retry({
					count: 3,
					delay: (error, retryCount) =>
						isRetryable(error) ? timer(500 * 2 ** (retryCount - 1)) : throwError(() => error),
				}),
				map(response => {
					const token = response?.result?.data?.token;
					if (!token) {
						throw new HelixSwapError();
					}
					return token;
				})
			);
	}

	/**
	 * Writes the pair helix boots from and the partner mirror. The `token` mirror is `AuthService`'s to
	 * write (`setToken`), so this app's memory and session caches agree with it.
	 */
	storeSession({ token, partnerName }: HelixSession): void {
		this.localStorage.set(HELIX_SESSION_STORAGE_KEY, JSON.stringify({ token, partnerName }));
		this.localStorage.set('partnerName', partnerName);
	}

	/** The pair as stored, by this app or by helix itself; `null` when absent or unreadable. */
	readSession(): HelixSession | null {
		try {
			const stored: unknown = JSON.parse(this.localStorage.get(HELIX_SESSION_STORAGE_KEY) ?? 'null');
			if (
				typeof stored === 'object' &&
				stored !== null &&
				typeof (stored as HelixSession).token === 'string' &&
				typeof (stored as HelixSession).partnerName === 'string'
			) {
				return { token: (stored as HelixSession).token, partnerName: (stored as HelixSession).partnerName };
			}
		} catch {
			// Corrupt JSON reads as no pair; helix reports it at boot.
		}
		return null;
	}
}
